// src/pages/MasterData/MasterDataPlaceholderPage.jsx
// =============================================================================
// Generic Master Data placeholder page
// =============================================================================
//
// PURPOSE
// -------
// The new Master Data sidebar menu already defines several future business
// configuration areas. Only Company Master Data is implemented as the first
// structured page in this batch.
//
// This lightweight placeholder component keeps the navigation complete without
// pretending that backend persistence already exists.
//
// Why this is better than disabled menu items:
// - the user can see the planned module structure immediately,
// - routes are stable from the beginning,
// - future implementation can replace this placeholder per route without
//   changing Sidebar/App.js again,
// - QA can verify navigation and translations now.

import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./MasterDataPage.css";

const VALID_SECTION_KEYS = new Set([
  "documentSettings",
  "paymentMethods",
  "taxSettings",
  "salesNumberSeries",
  "locationsBranches",
  "legalTexts",
]);

function readTranslatedArray(t, key) {
  const value = t(key, { returnObjects: true });
  return Array.isArray(value) ? value : [];
}

function MasterDataPlaceholderPage({ sectionKey }) {
  const { t } = useTranslation(["masterData", "navigation", "common"]);
  const navigate = useNavigate();

  // Defensive fallback. App.js passes a controlled sectionKey, but keeping the
  // fallback makes the component safer during future route refactoring.
  const safeSectionKey = VALID_SECTION_KEYS.has(sectionKey)
    ? sectionKey
    : "documentSettings";

  const plannedItems = readTranslatedArray(
    t,
    `masterData:placeholders.${safeSectionKey}.items`
  );

  return (
    <div className="master-data-page">
      <div className="master-data-page__inner master-data-page__inner--narrow">
        <header className="master-data-header">
          <div>
            <p className="master-data-eyebrow">
              {t("navigation:masterData")}
            </p>
            <h1>{t(`masterData:placeholders.${safeSectionKey}.title`)}</h1>
            <p className="master-data-subtitle">
              {t(`masterData:placeholders.${safeSectionKey}.subtitle`)}
            </p>
          </div>

          <span className="master-data-status-chip">
            {t("masterData:common.plannedArea")}
          </span>
        </header>

        <section className="master-data-card">
          <div className="master-data-card__header">
            <div>
              <h2>{t("masterData:placeholders.common.scopeTitle")}</h2>
              <p>{t("masterData:placeholders.common.scopeDescription")}</p>
            </div>
          </div>

          <div className="master-data-planned-list">
            {plannedItems.map((item, index) => (
              <article key={`${safeSectionKey}-${index}`} className="master-data-planned-item">
                <span>{index + 1}</span>
                <p>{item}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="master-data-warning-card">
          <strong>{t("masterData:placeholders.common.backendTitle")}</strong>
          <p>{t("masterData:placeholders.common.backendDescription")}</p>
        </section>

        <button
          type="button"
          className="master-data-secondary-button"
          onClick={() => navigate("/master-data/company")}
        >
          {t("masterData:placeholders.common.backToCompany")}
        </button>
      </div>
    </div>
  );
}

export default MasterDataPlaceholderPage;
